import { useEffect, useState } from 'react';
import { api, ApiClient } from '@/lib/api-client';

// Module-level cache: the JWT lacks a `roles` claim (auth.routes.ts signToken),
// so we fetch /auth/me once per session and reuse the answer.
let cached: { roles: string[]; isSuperuser: boolean; loaded: boolean } | null = null;
let inFlight: Promise<{ roles: string[]; isSuperuser: boolean }> | null = null;

function isSuperuserFromToken(): boolean {
  const token = ApiClient.getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.superuser === true;
  } catch {
    return false;
  }
}

async function load(): Promise<{ roles: string[]; isSuperuser: boolean }> {
  if (cached?.loaded) return { roles: cached.roles, isSuperuser: cached.isSuperuser };
  if (inFlight) return inFlight;
  const isSuper = isSuperuserFromToken();
  inFlight = (async () => {
    if (isSuper) {
      const result = { roles: ['superuser'], isSuperuser: true };
      cached = { ...result, loaded: true };
      return result;
    }
    try {
      const me = (await api.getMe()) as { roles?: string[]; isPlatformAdmin?: boolean };
      const roles = Array.isArray(me?.roles) ? me.roles : [];
      // A Cornerstone platform admin carries authority in the `is_platform_admin`
      // claim, not in app_roles. The old path only looked for a `superuser` claim
      // on a locally-issued token, so an SSO admin arrived with no roles and no
      // superuser and was silently bounced out of every gated route.
      const result = { roles, isSuperuser: me?.isPlatformAdmin === true };
      cached = { ...result, loaded: true };
      return result;
    } catch {
      const result = { roles: [], isSuperuser: false };
      cached = { ...result, loaded: true };
      return result;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

interface UseCurrentUserRolesResult {
  roles: string[];
  isSuperuser: boolean;
  isLeadership: boolean;
  loading: boolean;
}

export function useCurrentUserRoles(): UseCurrentUserRolesResult {
  const [state, setState] = useState<{ roles: string[]; isSuperuser: boolean; loading: boolean }>(
    () =>
      cached?.loaded
        ? { roles: cached.roles, isSuperuser: cached.isSuperuser, loading: false }
        : { roles: [], isSuperuser: false, loading: true },
  );

  useEffect(() => {
    if (cached?.loaded) return;
    let cancelled = false;
    load().then((r) => {
      if (cancelled) return;
      setState({ roles: r.roles, isSuperuser: r.isSuperuser, loading: false });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const isLeadership =
    state.isSuperuser || state.roles.some((r) => r === 'pastor' || r === 'admin');

  return { roles: state.roles, isSuperuser: state.isSuperuser, isLeadership, loading: state.loading };
}
